'use client';

import { useSearchParams, useRouter } from 'next/navigation';
import { useState, Suspense } from 'react';
import { getEventById, mockEvents } from '@/lib/mock-data';
import { BookingStepper } from '@/components/BookingStepper';
import { EmptyState } from '@/components/EmptyState';
import { Calendar, MapPin, Users, Mail, Phone, CreditCard, Shield } from 'lucide-react';
import Image from 'next/image';

function BookingContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const eventId = searchParams.get('event');
  const packageId = searchParams.get('package');

  const event = eventId ? getEventById(eventId) : null;
  const selectedPackage = event?.packages.find((pkg) => pkg.id === packageId);

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Personal Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    guestCount: 1,
    specialRequests: '',

    // Step 2: Payment (mock)
    walletAddress: '',
    agreedToTerms: false,
  });

  const steps = ['Your Details', 'Review', 'Payment'];

  if (!event || !selectedPackage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] px-4 py-24">
        <div className="mx-auto max-w-7xl">
          <EmptyState
            title="Invalid Booking"
            description="The event or package you selected is not available. Please return to browse events."
            actionLabel="Browse Events"
            actionHref="/events"
          />
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const totalPrice = selectedPackage.priceUSDT * formData.guestCount;

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      // Complete booking - redirect to confirmation
      router.push(
        `/booking/confirmation?event=${eventId}&package=${packageId}&guests=${formData.guestCount}`
      );
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    if (currentStep === 1) {
      return (
        formData.firstName &&
        formData.lastName &&
        formData.email &&
        formData.phone &&
        formData.guestCount > 0 &&
        formData.guestCount <= selectedPackage.maxGuests
      );
    }
    if (currentStep === 2) {
      return true; // Can always proceed from review
    }
    if (currentStep === 3) {
      return formData.walletAddress && formData.agreedToTerms;
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F] px-4 py-24">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="mb-4 font-serif text-4xl font-bold text-white md:text-5xl">
            Complete Your{' '}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#B76E79] bg-clip-text text-transparent">
              VIP Booking
            </span>
          </h1>
          <p className="text-gray-400">Just a few steps to secure your premium experience</p>
        </div>

        {/* Stepper */}
        <BookingStepper currentStep={currentStep} steps={steps} />

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Form Area */}
          <div className="lg:col-span-2">
            <div className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/60 to-black/40 p-8 backdrop-blur-md">
              {/* Step 1: Personal Details */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h2 className="mb-6 font-serif text-2xl font-bold text-white">
                    Personal Details
                  </h2>

                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-300">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                        placeholder="John"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-300">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#D4AF37]" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 pl-12 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                        placeholder="john.doe@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#D4AF37]" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 pl-12 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                        placeholder="+880 1234 567890"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Number of Guests * (max {selectedPackage.maxGuests})
                    </label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#D4AF37]" />
                      <input
                        type="number"
                        min="1"
                        max={selectedPackage.maxGuests}
                        value={formData.guestCount}
                        onChange={(e) =>
                          setFormData({ ...formData, guestCount: parseInt(e.target.value) || 1 })
                        }
                        className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 pl-12 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Special Requests (Optional)
                    </label>
                    <textarea
                      value={formData.specialRequests}
                      onChange={(e) =>
                        setFormData({ ...formData, specialRequests: e.target.value })
                      }
                      rows={4}
                      className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                      placeholder="Any dietary restrictions, accessibility needs, or special occasions..."
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Review */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="mb-6 font-serif text-2xl font-bold text-white">
                    Review Your Booking
                  </h2>

                  <div className="space-y-4">
                    <div className="rounded-lg border border-[#D4AF37]/20 bg-black/40 p-6">
                      <h3 className="mb-4 font-semibold text-white">Personal Information</h3>
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-300">
                          <span className="text-gray-500">Name:</span> {formData.firstName}{' '}
                          {formData.lastName}
                        </p>
                        <p className="text-gray-300">
                          <span className="text-gray-500">Email:</span> {formData.email}
                        </p>
                        <p className="text-gray-300">
                          <span className="text-gray-500">Phone:</span> {formData.phone}
                        </p>
                        <p className="text-gray-300">
                          <span className="text-gray-500">Guests:</span> {formData.guestCount}
                        </p>
                        {formData.specialRequests && (
                          <p className="text-gray-300">
                            <span className="text-gray-500">Special Requests:</span>{' '}
                            {formData.specialRequests}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="rounded-lg border border-[#D4AF37]/20 bg-black/40 p-6">
                      <h3 className="mb-4 font-semibold text-white">Event Details</h3>
                      <div className="space-y-2 text-sm text-gray-300">
                        <p>
                          <span className="text-gray-500">Event:</span> {event.artistName} -{' '}
                          {event.eventTitle}
                        </p>
                        <p>
                          <span className="text-gray-500">Date:</span> {formatDate(event.date)}
                        </p>
                        <p>
                          <span className="text-gray-500">Venue:</span> {event.venue},{' '}
                          {event.city}
                        </p>
                        <p>
                          <span className="text-gray-500">Package:</span> {selectedPackage.name}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Payment */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <h2 className="mb-6 font-serif text-2xl font-bold text-white">
                    Payment Details
                  </h2>

                  <div className="rounded-lg border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6">
                    <div className="mb-4 flex items-center gap-3">
                      <Shield className="h-6 w-6 text-[#D4AF37]" />
                      <h3 className="font-semibold text-white">Secure USDT Payment</h3>
                    </div>
                    <p className="text-sm text-gray-300">
                      We accept USDT (Tether) cryptocurrency payments for fast, secure, and
                      borderless transactions.
                    </p>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-300">
                      Your USDT Wallet Address *
                    </label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#D4AF37]" />
                      <input
                        type="text"
                        value={formData.walletAddress}
                        onChange={(e) =>
                          setFormData({ ...formData, walletAddress: e.target.value })
                        }
                        className="w-full rounded-lg border border-[#D4AF37]/20 bg-black/40 px-4 py-3 pl-12 font-mono text-sm text-white backdrop-blur-md transition-all duration-300 placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30"
                        placeholder="0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb"
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-400">
                      Enter your USDT wallet address (ERC-20, TRC-20, or BEP-20)
                    </p>
                  </div>

                  <div className="rounded-lg border border-[#D4AF37]/20 bg-black/40 p-6">
                    <h3 className="mb-4 font-semibold text-white">Payment Instructions</h3>
                    <ol className="list-inside list-decimal space-y-2 text-sm text-gray-300">
                      <li>Enter your USDT wallet address above</li>
                      <li>
                        Send exactly <strong className="text-[#D4AF37]">{totalPrice} USDT</strong>{' '}
                        to our payment address
                      </li>
                      <li>Wait for blockchain confirmation (2-5 minutes)</li>
                      <li>Receive instant booking confirmation via email</li>
                    </ol>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={formData.agreedToTerms}
                      onChange={(e) =>
                        setFormData({ ...formData, agreedToTerms: e.target.checked })
                      }
                      className="mt-1 h-4 w-4 rounded border-[#D4AF37]/20 bg-black/40 text-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30"
                    />
                    <label htmlFor="terms" className="text-sm text-gray-300">
                      I agree to the{' '}
                      <a href="/terms" className="text-[#D4AF37] hover:underline">
                        Terms & Conditions
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" className="text-[#D4AF37] hover:underline">
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="mt-8 flex justify-between gap-4">
                {currentStep > 1 && (
                  <button
                    onClick={handleBack}
                    className="rounded-lg border border-[#D4AF37]/30 px-8 py-3 font-semibold text-white transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="ml-auto rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#B76E79] px-8 py-3 font-semibold text-black transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                >
                  {currentStep === 3 ? 'Complete Booking' : 'Continue'}
                </button>
              </div>
            </div>
          </div>

          {/* Booking Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-black/80 to-black/60 p-6 backdrop-blur-md">
              <h3 className="mb-6 font-serif text-xl font-bold text-white">Booking Summary</h3>

              {/* Event Image */}
              <div className="mb-6 overflow-hidden rounded-lg">
                <Image
                  src={event.imageUrl}
                  alt={event.eventTitle}
                  width={400}
                  height={200}
                  className="aspect-video object-cover"
                />
              </div>

              {/* Event Info */}
              <div className="mb-6 space-y-3 text-sm">
                <h4 className="font-semibold text-white">{event.artistName}</h4>
                <div className="flex items-center gap-2 text-gray-400">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(event.date)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-400">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {event.venue}, {event.city}
                  </span>
                </div>
              </div>

              <div className="mb-6 border-t border-[#D4AF37]/20 pt-6">
                <p className="mb-2 text-sm text-gray-400">Package</p>
                <p className="font-semibold text-white">{selectedPackage.name}</p>
                <p className="mt-1 text-sm text-[#D4AF37]">{selectedPackage.tier} Tier</p>
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3 border-t border-[#D4AF37]/20 pt-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Price per guest</span>
                  <span className="text-white">
                    {selectedPackage.priceUSDT.toLocaleString()} USDT
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Number of guests</span>
                  <span className="text-white">×{formData.guestCount}</span>
                </div>
                <div className="flex justify-between border-t border-[#D4AF37]/20 pt-3 text-lg font-bold">
                  <span className="text-white">Total</span>
                  <span className="text-[#D4AF37]">{totalPrice.toLocaleString()} USDT</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BookingPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <BookingContent />
    </Suspense>
  );
}
